import {
    AssignmentResponse,
    HighlightedResponse,
    PortfolioApiResult,
    PortfolioData,
    SlotsOrder,
    UpdateValidityResponse,
} from './user_portfolio.types';
import { emptyPortfolioData } from './user_portfolio.initial';

// El servicio vive en el microservicio de métodos de pago (mismo host que
// `suggested_calculator`). Ej:
//   {BASE}/commercial_panel/portfolio?country=MX&parameter=<email>&page=0
const BASE_URL = process.env.REACT_APP_API_PAYMENT_METHODS ?? '';
const API_KEY = process.env.REACT_APP_XAPIKEY_TRANSACTION_AUTHORIZE ?? '';

interface ApiEnvelope {
    success?: boolean;
    errors?: { code?: number; message?: string; json_message?: unknown };
    data?: Partial<PortfolioData>;
}

function headers(token: string): Record<string, string> {
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-api-key': API_KEY,
    };
}

/**
 * Helper común para los POST de `commercial_panel/*`: arma la URL (`?country=MX`),
 * envía el body con `country` incluido y normaliza el manejo de errores (HTTP y
 * `success: false`). Centraliza el patrón compartido por validity/manage/cancel/
 * highlighted para evitar duplicación.
 */
async function postCommercialPanel(
    token: string,
    path: string,
    body: object,
): Promise<{ success: true; message?: string }> {
    const url = `${BASE_URL}${path}?country=MX`;
    const res = await fetch(url, {
        method: 'POST',
        headers: headers(token),
        body: JSON.stringify({ country: 'MX', ...body }),
    });

    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${text}`);
    }

    const json = (await res.json()) as ApiEnvelope & { success?: boolean };
    if (json.success === false) {
        throw new Error(json.errors?.message || 'Error del servidor');
    }

    return { success: true, message: json.errors?.message };
}

function normalizeData(data: Partial<PortfolioData> | undefined): PortfolioData {
    const properties = data?.properties;
    const slots = data?.slots;
    return {
        data_user: data?.data_user ?? null,
        totalProperties: data?.totalProperties ?? 0,
        totalStatus: data?.totalStatus ?? 0,
        properties: Array.isArray(properties) ? properties : [],
        // Garantiza que cada slot exponga `package_slot_category_id` (8 = Prime,
        // 1 = Destacado); si el backend no lo envía, queda en `null`.
        slots: Array.isArray(slots)
            ? slots.map((slot) => ({
                  ...slot,
                  package_slot_category_id: slot?.package_slot_category_id ?? null,
              }))
            : [],
        totalSlots: data?.totalSlots ?? 0,
        userType: data?.userType ?? '',
    };
}

/**
 * GET `commercial_panel/portfolio` — portafolio del usuario (propiedades, slots,
 * datos del usuario y totales).
 *
 * `parameter` suele ser el correo del usuario buscado. El backend usa un único
 * `page` tanto para slots como para propiedades y `page_size` controla la
 * cantidad de elementos por página (máx. 100). Los filtros opcionales acotan por
 * un id específico: `slot_id` para slots e `id` para propiedades. `slots_order`
 * (asc/desc) ordena los slots por fecha de expiración.
 */
export const getPortfolioApi = async (
    token: string,
    parameter: string,
    page = 0,
    slotId: string | number = '',
    propertyId: string | number = '',
    slotsOrder: SlotsOrder | null = null,
    pageSize?: number,
): Promise<PortfolioApiResult> => {
    const normalizedSlotId = String(slotId ?? '').trim();
    const normalizedPropertyId = String(propertyId ?? '').trim();
    const params = new URLSearchParams({
        country: 'MX',
        parameter,
        page: String(page),
    });
    if (pageSize != null) {
        params.set('page_size', String(pageSize));
    }
    if (normalizedSlotId !== '') {
        params.set('slot_id', normalizedSlotId);
    }
    if (normalizedPropertyId !== '') {
        params.set('id', normalizedPropertyId);
    }
    if (slotsOrder) {
        params.set('slots_order', slotsOrder);
    }

    const url = `${BASE_URL}commercial_panel/portfolio?${params.toString()}`;
    const res = await fetch(url, { method: 'GET', headers: headers(token) });

    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${text}`);
    }

    const json = (await res.json()) as ApiEnvelope;
    if (json.success === false) {
        throw new Error(json.errors?.message || 'Error del servidor');
    }

    const data = json.data ? normalizeData(json.data) : { ...emptyPortfolioData };

    return {
        parameter,
        page,
        pageSize: pageSize ?? null,
        slotId: normalizedSlotId,
        propertyId: normalizedPropertyId,
        slotsOrder: slotsOrder ?? null,
        data,
    };
};

interface ValidityRequestBody {
    /** Slots a modificar. */
    slot_id: number[];
    /** Nueva fecha de expiración (YYYY-MM-DD). */
    date_end: string;
    observations: string;
}

/**
 * POST `commercial_panel/validity` — cambia la vigencia de los slots seleccionados.
 *
 * Envía los slots elegidos junto con la nueva fecha de expiración y las observaciones.
 */
export const updateValidityApi = (
    token: string,
    body: ValidityRequestBody,
): Promise<UpdateValidityResponse> =>
    postCommercialPanel(token, 'commercial_panel/validity', body);

/** Item de asignación: vincula un slot con una propiedad. */
export interface ManageSlotItem {
    slot_id: number;
    property_id: number;
    observations: string;
    sales_panel: boolean;
    /** Propiedad previa del slot; presente activa el escenario SWAP en el backend. */
    old_property_id?: number;
}

interface ManageSlotRequestBody {
    /** Lista de asignaciones slot -> propiedad. */
    items: ManageSlotItem[];
}

/**
 * POST `commercial_panel/slots/manage` — asigna uno o más slots a una propiedad.
 *
 * Body: `{ country, items: [{ slot_id, property_id, observations, sales_panel }] }`.
 */
export const manageSlotApi = (
    token: string,
    body: ManageSlotRequestBody,
): Promise<AssignmentResponse> =>
    postCommercialPanel(token, 'commercial_panel/slots/manage', body);

interface HighlightedRequestBody {
    /** Número de slots a crear. */
    slots: number;
    /** Vigencia de cada slot, en meses. */
    months: number;
    /** Usuario para el que se crean los slots. */
    id_user: number;
    /** Tipo de slot: 1 = destacado, 8 = prime. */
    highlighted_type: number;
}

/**
 * POST `commercial_panel/highlighted` — crea slots comerciales (destacado/prime)
 * para el usuario.
 *
 * Body: `{ country, slots, months, id_user, highlighted_type }`
 * (`highlighted_type`: 1 = destacado, 8 = prime).
 */
export const createHighlightedSlotsApi = (
    token: string,
    body: HighlightedRequestBody,
): Promise<HighlightedResponse> =>
    postCommercialPanel(token, 'commercial_panel/highlighted', body);

interface CancelSlotRequestBody {
    /** Propiedades cuyas asignaciones se liberan. */
    properties_ids: number[];
    /** Slots a liberar. */
    slots_ids: number[];
    observations: string;
    sales_panel: boolean;
}

/**
 * POST `commercial_panel/cancel` — libera (cancela la asignación de) slots y/o
 * propiedades.
 *
 * Body: `{ country, properties_ids, slots_ids, observations, sales_panel }`.
 */
export const cancelSlotApi = (
    token: string,
    body: CancelSlotRequestBody,
): Promise<AssignmentResponse> =>
    postCommercialPanel(token, 'commercial_panel/cancel', body);
